<i18n
  src="@/locales/views/packing_house/quality_control/process/new_inspect.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/quality_inspect.yml" lang="yaml"></i18n>
<template>
  <div>
    <go-back-link
      :text="$t('labels.go_back_link')"
      name="ph_quality_control_process_list_inspects_path"
      :params="{
        packing_house_id: packing_house_id,
        packing_house_process_id: packing_house_process_id,
        packing_process_quality_template_id:
          packing_process_quality_template_id,
        harvested_batch_id: harvested_batch_id,
      }"
    />

    <bread-crumbs
      :labels="[
        module_name,
        packing_house_process?.name,
        harvested_batch?.batch_code,
        template?.name,
      ]"
    ></bread-crumbs>
    <harvested-batch-info
      v-if="harvested_batch"
      :harvested_batch="harvested_batch"
    />
    <loading-message v-else />
    <div class="mt-8">
      <div class="panel">
        <div class="panel-header">
          <h3 class="panel-header-title">
            {{ $t("labels.title") }}
          </h3>
        </div>

        <div class="panel-body">
          <div
            class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 w-full pb-6"
          >
            <div class="sm:col-span-3">
              <label
                for="first-name"
                class="block text-sm font-medium text-gray-700"
              >
                {{ $t("model.quality_inspect.started_at") }}
              </label>
              <div class="mt-1 relative rounded-md shadow-sm">
                <date-form
                  v-if="render_started_at"
                  v-model="started_at"
                  mode="dateTime"
                  :first-day-of-week="2"
                  locale="es"
                  show-iso-weeknumbers
                ></date-form>
              </div>
            </div>
            <div class="sm:col-span-3">
              <template v-if="render_finished_at">
                <label
                  for="first-name"
                  class="block text-sm font-medium text-gray-700"
                >
                  {{ $t("model.quality_inspect.finished_at") }}
                </label>
                <div class="mt-1 relative rounded-md shadow-sm">
                  <date-form
                    v-model="finished_at"
                    mode="dateTime"
                    :first-day-of-week="2"
                    locale="es"
                    show-iso-weeknumbers
                  ></date-form>
                </div>
              </template>
            </div>
            <div class="sm:col-span-3" v-if="template?.control_exportation">
              <label
                for="origin_export_id"
                class="block text-sm font-medium text-gray-700"
              >
                Productor(a)
              </label>
              <div class="mt-1">
                <select-form
                  v-if="exported_batch_options.length > 0"
                  v-model="exported_batch_id"
                  :options="exported_batch_options"
                />
              </div>
            </div>
            <div
              class="sm:col-span-3"
              v-if="
                template &&
                template.use_samplings &&
                !template?.specific_sampling
              "
            >
              <label
                for="first-name"
                class="block text-sm font-medium text-gray-700"
              >
                {{ $t("model.quality_inspect.samplings") }}
              </label>
              <div class="mt-1">
                <input
                  type="text"
                  v-model.lazy.number="samplings"
                  class="input-text"
                />
              </div>
            </div>
          </div>
          <template v-if="template">
            <sampling-layout
              v-if="template.use_samplings"
              v-model="quality_samplings"
              :samplings="samplings"
              :quality_template="template"
            />
            <general-layout
              v-else
              v-model="quality_generals"
              :quality_template="template"
            />
          </template>
        </div>
        <div class="panel-footer">
          <div class="panel-footer-actions">
            <white-button
              @click.prevent="goToTemplate()"
              :label="$t('labels.cancel_button')"
            />
            <span class="space-actions"></span>
            <success-button
              v-if="!charging_submit"
              @click.prevent="submit()"
              :label="$t('labels.save_button')"
            />
            <success-button-processing
              v-else
              :label="$t('labels.save_button')"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Requester from "@/views/packing_house/requesters/quality_control/process/new_inspect.js";
import HarvestedBatchInfo from "@/components/views/packing_house/quality_control/process/HarvestedBatchInfo.vue";
import GoBackLink from "@/components/views/layout/GoBackLink";
import SamplingLayout from "@/components/views/layout/quality_control/SamplingLayout.vue";
import GeneralLayout from "@/components/views/layout/quality_control/GeneralLayout.vue";

import moment from "moment";
import BreadCrumbs from "@/components/views/layout/BreadCrumbs.vue";

export default {
  components: {
    HarvestedBatchInfo,
    GoBackLink,
    BreadCrumbs,
    GeneralLayout,
    SamplingLayout,
  },
  props: {
    harvested_batch_id: {
      required: true,
    },
    packing_process_quality_template_id: {
      required: true,
    },
    packing_house_id: {
      required: true,
    },
    packing_house_process_id: {
      required: true,
    },
    current_tab: {},
  },
  data() {
    return {
      charging_submit: false,
      render_started_at: false,
      render_finished_at: false,
      packing_house_process: null,
      harvested_batch: null,
      template: null,
      samplings: null,
      started_at: null,
      finished_at: null,
      exported_batch: null,
      exported_batch_id: null,
      exported_batches: [],
      exported_batch_options: [],
      quality_generals: [],
      quality_samplings: [],
    };
  },
  computed: {
    can_send: function () {
      return this.valid_data && !this.charging_submit && this.template;
    },
    started_at_formatted: function () {
      let started_at = this.started_at;
      if (started_at) return moment(started_at).format("YYYY-MM-DD HH:mm A");
      return "";
    },
    finished_at_formatted: function () {
      let finished_at = this.finished_at;
      if (finished_at) return moment(finished_at).format("YYYY-MM-DD HH:mm A");
      return "";
    },
    save_link_class: function () {
      let basic_class = "btn-main";
      if (this.can_send) return basic_class;
      return `${basic_class}  opacity-50 cursor-not-allowed`;
    },
    valid_data: function () {
      return (
        Boolean(this.started_at) &&
        (Boolean(this.samplings) || !this.template?.use_samplings)
      );
    },
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          packing_house_id: this.packing_house_id,
        },
      }).then(() => {
        this.setDefaultValues();
        this.checkIfAuthenticated();
        this.fetchPackingHouseProcess();
        this.fetchHarvestedBatch();
        this.fetchTemplate().then(() => {
          // if (this.template?.control_exportation) this.fetchExportedBatches();
        });
      })
    );
  },
  methods: {
    setDefaultValues: async function () {
      let now = moment();
      this.started_at = now.format("YYYY-MM-DD HH:mm");
      this.render_started_at = true;
    },
    fetchPackingHouseProcess: async function () {
      let response = await Requester.fetchPackingHouseProcess({
        packing_house_id: this.packing_house_id,
        packing_house_process_id: this.packing_house_process_id,
      });
      this.packing_house_process = response.data;
    },
    exportedBatchOption: function (exported_batch) {
      let label = "";
      if (exported_batch.producer) {
        label += exported_batch.producer;
        if (exported_batch.organization)
          label += ` (${exported_batch.organization})`;
      } else label += exported_batch.organization;
      return {
        name: label,
        id: exported_batch.id,
      };
    },
    fetchHarvestedBatch: async function () {
      let response = await Requester.fetchHarvestedBatch({
        packing_house_id: this.packing_house_id,
        packing_house_process_id: this.packing_house_process_id,
        harvested_batch_id: this.harvested_batch_id,
      });
      this.harvested_batch = response.data;
    },
    fetchExportedBatches: async function () {
      let response = await Requester.fetchExportedBatches({
        harvested_batch_id: this.harvested_batch_id,
      });
      this.exported_batches = response.data;
      this.exported_batches.forEach((exported_batch) => {
        this.exported_batch_options.push(
          this.exportedBatchOption(exported_batch)
        );
      });
    },
    fetchTemplate: async function () {
      let response = await Requester.fetchTemplate({
        packing_process_quality_template_id:
          this.packing_process_quality_template_id,
        harvested_batch_id: this.harvested_batch_id,
        packing_house_id: this.packing_house_id,
        packing_house_process_id: this.packing_house_process_id,
      });
      this.template = response.data;
      if (this.template?.specific_sampling)
        this.samplings = this.template?.samplings;
    },
    goToTemplate: function () {
      this.$router.push({
        name: "ph_quality_control_process_list_inspects_path",
        params: {
          packing_house_id: this.packing_house_id,
          harvested_batch_id: this.harvested_batch_id,
          packing_house_process_id: this.packing_house_process_id,
          packing_process_quality_template_id:
            this.packing_process_quality_template_id,
        },
      });
    },
    submit: async function () {
      if (!this.can_send) return;
      this.charging_submit = true;
      if (this.template?.use_samplings) await this.submit_with_samplings();
      else await this.submit_without_samplings();
      this.charging_submit = false;
    },
    submit_with_samplings: async function () {
      try {
        let response = await Requester.createWithSamplings({
          packing_house_id: this.packing_house_id,
          packing_house_process_id: this.packing_house_process_id,
          harvested_batch_id: this.harvested_batch_id,
          packing_process_quality_template_id:
            this.packing_process_quality_template_id,
          started_at: this.started_at_formatted,
          finished_at: this.finished_at_formatted,
          exported_batch_id: this.exported_batch_id,
          samplings: this.samplings,
          quality_samplings: this.quality_samplings,
        });
        this.registerSuccessNotification({
          title: response.data.success_message,
        });
        this.goToTemplate();
      } catch (error) {
        console.log(error);
      }
    },
    submit_without_samplings: async function () {
      try {
        let response = await Requester.createWithoutSamplings({
          packing_house_id: this.packing_house_id,
          packing_house_process_id: this.packing_house_process_id,
          harvested_batch_id: this.harvested_batch_id,
          packing_process_quality_template_id:
            this.packing_process_quality_template_id,
          started_at: this.started_at_formatted,
          finished_at: this.finished_at_formatted,
          exported_batch_id: this.exported_batch_id,
          samplings: this.samplings,
          quality_generals: this.quality_generals,
        });
        this.registerSuccessNotification({
          title: response.data.success_message,
        });
        this.goToTemplate();
      } catch (error) {
        console.log(error);
      }
    },
  },
  watch: {
    started_at: {
      handler: function (started_at) {
        this.render_finished_at = false;
        if (!started_at) return;
        started_at = moment(started_at);
        this.finished_at = started_at
          .add(1, "hours")
          .format("YYYY-MM-DD HH:mm");
        this.render_finished_at = true;
      },
    },
  },
};
</script>
